import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { ArrowLeft, Search, TrendingUp, TrendingDown, Fuel, Calendar, Building } from 'lucide-react';
import { toast } from 'sonner';

const UtilitiesPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('ifsc');
  
  // IFSC State
  const [ifscCode, setIfscCode] = useState('');
  const [ifscResult, setIfscResult] = useState(null);
  
  // Rates State
  const [metalRates, setMetalRates] = useState(null);
  const [fuelPrices, setFuelPrices] = useState(null);
  const [holidays, setHolidays] = useState(null);

  useEffect(() => {
    if (activeTab === 'gold-silver' && !metalRates) {
      fetchMetalRates();
    }
    if (activeTab === 'fuel' && !fuelPrices) {
      fetchFuelPrices();
    }
    if (activeTab === 'holidays' && !holidays) {
      fetchHolidays();
    }
  }, [activeTab]);

  const fetchMetalRates = async () => {
    try {
      const res = await axios.get(`${API}/rates/precious-metals`);
      setMetalRates(res.data);
    } catch (e) {
      toast.error('Failed to fetch rates');
    }
  };

  const fetchFuelPrices = async () => {
    try {
      const res = await axios.get(`${API}/rates/fuel`);
      setFuelPrices(res.data);
    } catch (e) {
      toast.error('Failed to fetch fuel prices');
    }
  };

  const fetchHolidays = async () => {
    try {
      const res = await axios.get(`${API}/holidays`);
      setHolidays(res.data);
    } catch (e) {
      toast.error('Failed to fetch holidays');
    }
  };

  const handleIFSCSearch = async () => {
    if (!ifscCode || ifscCode.length !== 11) {
      toast.error('Please enter valid 11-character IFSC code');
      return;
    }
    try {
      const res = await axios.get(`${API}/ifsc/${ifscCode.toUpperCase()}`);
      setIfscResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'IFSC not found');
      setIfscResult(null);
    }
  };

  const tabs = [
    { key: 'ifsc', label: 'IFSC Finder', icon: Search },
    { key: 'gold-silver', label: 'Gold & Silver Rates', icon: TrendingUp },
    { key: 'fuel', label: 'Fuel Prices', icon: Fuel },
    { key: 'holidays', label: 'Bank Holidays', icon: Calendar }
  ];

  return (
    <div style={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      <Navbar />

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '4rem 2rem' }}>
        <button 
          onClick={() => navigate('/')}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', color: '#FF6B35', cursor: 'pointer', marginBottom: '2rem', fontWeight: '600' }}
          data-testid="back-to-home-button"
        >
          <ArrowLeft size={20} /> Back to Home
        </button>

        <div style={{ marginBottom: '3rem' }}>
          <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '0.5rem', color: '#0F172A' }}>
            Utilities & Resources
          </h1>
          <p style={{ fontSize: '1.125rem', color: '#475569' }}>
            Helpful financial tools and real-time information at your fingertips
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={activeTab === tab.key ? 'primary-button' : 'outline-button'}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                data-testid={`utility-tab-${tab.key}`}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* IFSC Finder */}
        {activeTab === 'ifsc' && (
          <div className="calculator-section fade-in" data-testid="ifsc-section">
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#0F172A' }}>
              IFSC Code Finder
            </h2>
            <p style={{ color: '#475569', marginBottom: '2rem' }}>
              Find bank branch details using IFSC code
            </p>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="label-text">Enter IFSC Code</label>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <input
                  type="text"
                  className="input-field"
                  value={ifscCode}
                  onChange={(e) => setIfscCode(e.target.value.toUpperCase().slice(0, 11))}
                  placeholder="e.g., SBIN0001234"
                  maxLength={11}
                  data-testid="ifsc-input"
                />
                <button
                  onClick={handleIFSCSearch}
                  className="primary-button"
                  data-testid="ifsc-search-button"
                >
                  Search
                </button>
              </div>
            </div>

            {ifscResult && (
              <div style={{ marginTop: '2rem', padding: '2rem', backgroundColor: '#FFF7ED', borderRadius: '1rem' }} data-testid="ifsc-result">
                <h3 style={{ fontSize: '1.25rem', color: '#0F172A', marginBottom: '1rem' }}>
                  {ifscResult.bank_name}
                </h3>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  <div><strong style={{ color: '#475569' }}>IFSC:</strong> <span style={{ fontFamily: 'JetBrains Mono', color: '#FF6B35', fontWeight: '700' }}>{ifscResult.ifsc}</span></div>
                  <div><strong style={{ color: '#475569' }}>Branch:</strong> {ifscResult.branch}</div>
                  <div><strong style={{ color: '#475569' }}>City:</strong> {ifscResult.city}</div>
                  <div><strong style={{ color: '#475569' }}>State:</strong> {ifscResult.state}</div>
                  <div><strong style={{ color: '#475569' }}>Address:</strong> {ifscResult.address}</div>
                </div>
              </div>
            )}

            <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#F8FAFC', borderRadius: '0.75rem', border: '1px solid #E2E8F0' }}>
              <p style={{ fontSize: '0.875rem', color: '#475569' }}>
                <strong>Try:</strong> SBIN0001234, HDFC0001234, ICIC0001234, UTIB0001234
              </p>
            </div>
          </div>
        )}

        {/* Gold & Silver Rates */}
        {activeTab === 'gold-silver' && metalRates && (
          <div className="fade-in" data-testid="metals-section">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
              <div className="calculator-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.5rem', color: '#0F172A' }}>Gold Rate</h3>
                  <span style={{ color: metalRates.gold.trend === 'up' ? '#FF6B35' : '#EF4444', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    {metalRates.gold.trend === 'up' ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
                    {metalRates.gold.change_24h}
                  </span>
                </div>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div style={{ padding: '1rem', backgroundColor: '#FFF7ED', borderRadius: '0.75rem' }}>
                    <div style={{ fontSize: '0.875rem', color: '#475569' }}>24K Gold (10g)</div>
                    <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#FFB627' }}>₹{metalRates.gold['24k_10g'].toLocaleString()}</div>
                  </div>
                  <div style={{ padding: '1rem', backgroundColor: '#FFF7ED', borderRadius: '0.75rem' }}>
                    <div style={{ fontSize: '0.875rem', color: '#475569' }}>22K Gold (10g)</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#FFB627' }}>₹{metalRates.gold['22k_10g'].toLocaleString()}</div>
                  </div>
                  <div style={{ padding: '1rem', backgroundColor: '#FFF7ED', borderRadius: '0.75rem' }}>
                    <div style={{ fontSize: '0.875rem', color: '#475569' }}>18K Gold (10g)</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#FFB627' }}>₹{metalRates.gold['18k_10g'].toLocaleString()}</div>
                  </div>
                </div>
              </div>

              <div className="calculator-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.5rem', color: '#0F172A' }}>Silver Rate</h3>
                  <span style={{ color: metalRates.silver.trend === 'up' ? '#FF6B35' : '#EF4444', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    {metalRates.silver.trend === 'up' ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
                    {metalRates.silver.change_24h}
                  </span>
                </div>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div style={{ padding: '1rem', backgroundColor: '#FFF7ED', borderRadius: '0.75rem' }}>
                    <div style={{ fontSize: '0.875rem', color: '#475569' }}>Silver (1 kg)</div>
                    <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#475569' }}>₹{metalRates.silver['1kg'].toLocaleString()}</div>
                  </div>
                  <div style={{ padding: '1rem', backgroundColor: '#FFF7ED', borderRadius: '0.75rem' }}>
                    <div style={{ fontSize: '0.875rem', color: '#475569' }}>Silver (10g)</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#475569' }}>₹{metalRates.silver['10g'].toLocaleString()}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fuel Prices */}
        {activeTab === 'fuel' && fuelPrices && (
          <div className="calculator-section fade-in" data-testid="fuel-section">
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#0F172A' }}>
              Fuel Prices in India
            </h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#0F172A' }}>City</th>
                    <th style={{ padding: '1rem', textAlign: 'right', color: '#0F172A' }}>Petrol (₹/L)</th>
                    <th style={{ padding: '1rem', textAlign: 'right', color: '#0F172A' }}>Diesel (₹/L)</th>
                  </tr>
                </thead>
                <tbody>
                  {fuelPrices.cities.map((city, idx) => (
                    <tr key={city.city} style={{ borderBottom: '1px solid #E2E8F0', backgroundColor: idx % 2 === 0 ? 'white' : '#F8FAFC' }}>
                      <td style={{ padding: '1rem', fontWeight: '600', color: '#0F172A' }}>{city.city}</td>
                      <td style={{ padding: '1rem', textAlign: 'right', color: '#004D7A', fontWeight: '700' }}>₹{city.petrol.toFixed(2)}</td>
                      <td style={{ padding: '1rem', textAlign: 'right', color: '#FF6B35', fontWeight: '700' }}>₹{city.diesel.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Holidays */}
        {activeTab === 'holidays' && holidays && (
          <div className="calculator-section fade-in" data-testid="holidays-section">
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#0F172A' }}>
              Bank Holidays {holidays.year}
            </h2>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {holidays.holidays.map((holiday, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem', backgroundColor: idx % 2 === 0 ? '#FFF7ED' : 'white', borderRadius: '0.75rem', border: '1px solid #E2E8F0' }}>
                  <div>
                    <div style={{ fontWeight: '700', color: '#0F172A', marginBottom: '0.25rem' }}>{holiday.name}</div>
                    <div style={{ fontSize: '0.875rem', color: '#475569' }}>{holiday.type}</div>
                  </div>
                  <div style={{ fontFamily: 'Outfit', fontWeight: '700', color: '#FF6B35', fontSize: '1.125rem' }}>
                    {new Date(holiday.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UtilitiesPage;
